import { db } from '@/shared/db/database';
import { buildPhotoUrl } from '@/shared/utils/mapping';
import type { EmployeeDTO } from '@/modules/employees/infrastructure/dto/EmployeeDTO';

/**
 * Enrich any employee-like object with photoUrl by querying cached EmployeeDTO.
 *
 * Phase 6 Item 5: Photo hydration via offline-first cache.
 * This helper reads db.employees (IndexedDB) for the employee's DTO,
 * extracts photo_rekognition ?? photo, and builds the full URL.
 *
 * Used by:
 * - CompositeEmployeeLookupRepository.enrichPhotoUrl (internal)
 * - ScanningPage (before navigate to /success for face-matched employees)
 * - Any page that receives employee objects from sources other than Composite repo
 *
 * @param employee - Any object with idPerson (may or may not have photoUrl already)
 * @returns Employee object with photoUrl populated, or unchanged if cache miss/error
 */
export async function enrichEmployeePhotoUrl<T extends { idPerson: number }>(employee: T): Promise<T & { photoUrl?: string | null }> {
  try {
    const cached = await db.employees.where('idPerson').equals(employee.idPerson).first();
    if (!cached?.raw) return employee;

    const dto = cached.raw as EmployeeDTO;
    const photoFilename = dto.photo_rekognition ?? dto.photo;
    const photoUrl = buildPhotoUrl(photoFilename);

    return { ...employee, photoUrl };
  } catch {
    // Dexie error or missing cache → return employee as-is (photoUrl stays undefined)
    return employee;
  }
}
